import {useEffect} from 'react';
import {useQuery} from '@tanstack/react-query';
import {UserService} from '@/shared/services/userService';
import {queryClient} from '@/shared/contexts/QueryProvider';
import {setSubPatients, setCurrentPatient, selectCurrentPatient} from '@/slices/patientSlice';
import {useAppDispatch, useAppSelector} from '@/shared/hooks/redux';
import {useAuth} from '@/modules/auth/hooks/useAuth';
import {decodeJwtToken} from '@/shared/utils/tokenUtils';
import type {Test, Patient} from '@/shared/types/api';

export const USER_DATA_KEY = 'user-data';

/**
 * Shape of the `/api/user/data` payload. `UserService.getUserData()` types the
 * response as `{orders, categoryScores}`, but the BE also returns `subscription`
 * (the old `fetchTests` thunk read it via `(res as any)?.subscription`). We model
 * it here so `useSubscription` can resolve it without a cast.
 */
export interface UserDataResponse {
    orders: Test[];
    categoryScores: Record<string, number>;
    subscription?: boolean;
}

// ── pure helpers (unit-tested) ─────────────────────────────────────────

/**
 * Patient rows extracted from the `/api/user/data` orders. Matches the
 * `Patient` shape dispatched by the old thunk into `patientSlice` so
 * `setSubPatients`/`setCurrentPatient` accept it directly.
 */
export type DashboardPatient = Patient;

/**
 * Canonical name key: lowercased, hyphens/dashes folded to spaces, internal
 * whitespace collapsed. ELIS order rows and the users row routinely disagree
 * on hyphenation and spacing ("Jean-Paul" vs "Jean Paul"); a key that
 * preserves those differences strands the patient's results behind an entry
 * the UI never selects.
 */
export const normalizeNameKey = (name: string): string =>
    name
        .toLowerCase()
        .replace(/[-‐-―]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

const normalizeFullName = (firstName: string, lastName: string): string =>
    normalizeNameKey(`${firstName} ${lastName}`);

/**
 * Replicates the old `fetchTests` patient-extraction VERBATIM: one unique
 * patient per lowercased `${firstName} ${lastName}`, skipping rows missing a
 * first or last name, in first-seen order. `fullName` is the lowercased key
 * (matching the old thunk). Returns [] for empty/undefined orders.
 */
export function extractPatientsFromOrders(orders: Test[] | undefined): DashboardPatient[] {
    if (!orders || orders.length === 0) return [];

    const uniquePatients: Record<string, boolean> = {};
    const newPatients: DashboardPatient[] = [];

    for (let i = 0; i < orders.length; i++) {
        if (!orders[i].firstName || !orders[i].lastName) {
            continue;
        }
        const patient = normalizeFullName(orders[i].firstName, orders[i].lastName);
        if (!uniquePatients[patient]) {
            uniquePatients[patient] = true;
            newPatients.push({
                id: orders[i].id,
                firstName: orders[i].firstName?.trim(),
                lastName: orders[i].lastName?.trim(),
                fullName: patient || '',
                birthdate: orders[i].birthdate,
            });
        }
    }

    return newPatients;
}

/**
 * Decides whether the displayed patient should be swapped for one extracted
 * from the orders, returning the replacement or null to keep the selection.
 * The fallback exists so a stale/empty selection (e.g. a dependent whose
 * orders aged out) doesn't leave the dashboard blank — but it must never
 * demote the logged-in account holder: they are always legitimate to
 * display even with zero resulted orders in their own name (pending-only
 * orders, fresh impersonation). Order-extracted patients carry ORDER row
 * ids, so the account holder can only be recognized by their user id.
 */
export function resolveFallbackPatient(
    currentPatient: Patient | undefined,
    orderPatients: DashboardPatient[],
    accountHolderId: number | undefined
): DashboardPatient | null {
    if (orderPatients.length === 0) return null;

    const currentName =
        currentPatient?.firstName && currentPatient?.lastName
            ? normalizeFullName(currentPatient.firstName, currentPatient.lastName)
            : null;
    const existsInOrders =
        currentName != null &&
        orderPatients.some((p) => normalizeFullName(p.firstName ?? '', p.lastName ?? '') === currentName);
    if (existsInOrders) return null;

    if (currentPatient?.id && accountHolderId != null && currentPatient.id === accountHolderId) return null;

    return orderPatients.find((p) => p.id && p.id > 0 && p.firstName && p.lastName) ?? null;
}

/**
 * Replicates `selectTestsByName`: case/space-insensitive match on
 * `${firstName} ${lastName}`. Returns [] for empty name or data.
 */
export function filterTestsByName(orders: Test[] | undefined, fullName: string): Test[] {
    if (!orders || !fullName) return [];
    const normalizedName = normalizeNameKey(fullName);
    return orders.filter((t) => normalizeFullName(t.firstName ?? '', t.lastName ?? '') === normalizedName);
}

/** override wins (staging ?sub=), else API value, else true while loading. */
export function resolveSubscription(apiValue: boolean | undefined, override: boolean | null): boolean {
    if (override !== null) return override;
    if (apiValue === undefined) return true;
    return apiValue;
}

/**
 * Should the dashboard show its "Unable to load your health data" banner?
 * Only when the fetch failed AND there is nothing cached to render — a
 * failed background refetch over still-visible cached data would make the
 * banner contradict the page.
 */
export function showDataLoadError(isError: boolean, hasData: boolean): boolean {
    return isError && !hasData;
}

// ── staging ?sub= override (ported from testSlice) ─────────────────────

// Helper function to check if we're in staging or development environment
const isStagingEnvironment = (): boolean => {
    // Check environment variables
    const viteEnv = import.meta.env?.VITE_NODE_ENV;
    const nodeEnv = import.meta.env?.NODE_ENV;

    return viteEnv === 'staging' || viteEnv === 'development' || nodeEnv === 'staging' || nodeEnv === 'development';
};

// Helper function to get URL parameter value
const getUrlParameter = (name: string): string | null => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(name);
};

/**
 * Reads `?sub=true|false` in staging/dev only. Identical to the old
 * testSlice `getForceSubscriptionStatus`.
 */
function getForceSubscriptionStatus(): boolean | null {
    if (!isStagingEnvironment()) {
        return null; // Only work in staging/dev environments
    }

    const subParam = getUrlParameter('sub');
    if (subParam === null) {
        return null; // No 'sub' parameter found
    }

    // Convert string to boolean
    if (subParam.toLowerCase() === 'true') {
        return true;
    } else if (subParam.toLowerCase() === 'false') {
        return false;
    }

    return null; // Invalid value, use default behavior
}

// ── the query ──────────────────────────────────────────────────────────
export function useTestsQuery() {
    const {user} = useAuth();
    const userId = user?.userInfo?.id ?? null;
    const impersonateId = decodeJwtToken(user?.token ?? '')?.impersonate_id ?? null;
    return useQuery({
        queryKey: [USER_DATA_KEY, userId, impersonateId],
        queryFn: () => UserService.getUserData() as Promise<UserDataResponse>,
        enabled: Boolean(user?.isLoggedIn),
        // THE FIX: recover when the user returns to an idle tab.
        refetchOnWindowFocus: true,
    });
}

/** Drop-in for the old `selectTests` shape. */
export function useTestsState() {
    const q = useTestsQuery();
    const subscription = useSubscription();
    return {
        tests: q.data?.orders ?? [],
        loading: q.isLoading,
        isError: q.isError,
        categoryScores: q.data?.categoryScores ?? {},
        subscription,
    };
}

export function useTestsByName(fullName: string | undefined): Test[] {
    const q = useTestsQuery();
    return filterTestsByName(q.data?.orders, fullName ?? '');
}

export function useSubscription(): boolean {
    const q = useTestsQuery();
    return resolveSubscription(q.data?.subscription, getForceSubscriptionStatus());
}

/**
 * Mount ONCE (DashboardLayout). Runs the patient-extraction side effect the
 * old thunk did — into patientSlice — off the query data. Fallback selection
 * derives from testSlice L142-164, with one deliberate delta: it no longer
 * demotes the logged-in account holder (see resolveFallbackPatient).
 */
export function useTestsPatientSync(): void {
    const q = useTestsQuery();
    const dispatch = useAppDispatch();
    const currentPatient = useAppSelector(selectCurrentPatient);
    const {user} = useAuth();
    const accountHolderId = user?.userInfo?.id;
    const orders = q.data?.orders;
    useEffect(() => {
        if (!orders) return;

        const newPatients = extractPatientsFromOrders(orders);
        dispatch(setSubPatients(newPatients));

        const fallbackPatient = resolveFallbackPatient(currentPatient, newPatients, accountHolderId);
        if (fallbackPatient) {
            dispatch(setCurrentPatient(fallbackPatient));
        }
    }, [orders, dispatch, currentPatient?.firstName, currentPatient?.lastName, currentPatient?.id, accountHolderId]);
}

/** Imperative cache bust for logout / exit-impersonation / user-edit. */
export function resetTestsCache(): void {
    queryClient.removeQueries({queryKey: [USER_DATA_KEY]});
}
